/**
 * Domínio: UI global do app (navegação, topbar).
 *
 * Slots fora da área de atendimento — menu lateral, barra superior, overlay e
 * dashboard. Acesse via `app.slots.SIDEBAR_MENU`.
 *
 * Slots num registro só (`appSlots`); `slots`/`slotContext` derivados.
 */
import { defineSlotRegistry, legacySlotContextOf, slotNamesOf, t, type SlotContextMapOf } from "@/ui/slotSchema";

/**
 * O tema que o app está usando AGORA, para o plugin desenhar igual.
 *
 * Existe porque o HTML de um plugin é injetado no DOM do app e divide a tela
 * com ele: sem estas cores, a única saída do plugin é adivinhar. E adivinhar
 * errava — `@media (prefers-color-scheme: dark)` lê a preferência do SISTEMA
 * OPERACIONAL, enquanto o Markedesk escolhe o tema por conta própria
 * (localStorage + palette.type do MUI). Com o app no escuro e o Windows no
 * claro, o plugin pintava um painel branco no meio de uma tela preta.
 *
 * São as cores do próprio MUI, já resolvidas para o modo ativo — o plugin não
 * recalcula nada, só usa.
 */
export interface TemaDoApp {
  /** "light" | "dark" — o modo ativo, para decisões que a paleta não cobre. */
  modo: "light" | "dark";
  /** Cor de marca (palette.primary.main) — botões, links, destaques. */
  primaria: string;
  /** Texto legível SOBRE a cor primária (primary.contrastText). */
  primariaTexto: string;
  /** Fundo da superfície elevada, tipo card (palette.background.paper). */
  superficie: string;
  /** Fundo da página (palette.background.default). */
  fundo: string;
  /** Texto principal (palette.text.primary). */
  texto: string;
  /** Texto de apoio, menos contrastado (palette.text.secondary). */
  textoSecundario: string;
  /** Cor de divisórias e bordas (palette.divider). */
  borda: string;
  /** Verde de sucesso (palette.success.main) — "instalado", "conectado". */
  sucesso: string;
}

/** Descritor reutilizável do tema (page de Plugins e overlay recebem o mesmo). */
export const temaDesc = t.typed<TemaDoApp>("TemaDoApp");

/** Os pontos nativos da navbar não recebem contexto: empresa/usuário vêm do `slot.runtime`. */
const navbarItem = (what: string) => ({
  name: `navbar:${what}` as const,
  description: `Ponto nativo da barra superior (${what}); envolva para esconder/trocar.`,
  context: {},
});

export const appSlots = defineSlotRegistry({
  SIDEBAR_MENU: {
    name: "sidebar-menu",
    description:
      "Item no menu lateral de navegação; só actions, sem context nem wrap. O ITEM: ícone+rótulo, ou o plugin " +
      "desenha (render: 'jsx' + jsx, recebe props.open/collapsed; ou render: 'html'). O CLIQUE: openType: 'page' " +
      "= página inserida no app (/plugin/<id>/<action>, na sessão); resultType: 'jsx' + resultJsx = modal React; " +
      "senão iframe de routePath+endpoint.",
    context: {},
  },
  NAVBAR: {
    name: "navbar",
    description:
      "Barra superior. Itens de plugin sem ponto específico caem aqui (ao final). Cada item nativo tem também " +
      "o seu próprio slot (navbar:*).",
    context: {},
  },
  NAVBAR_SOCKET_STATUS: { ...navbarItem("socket-status"), description: "Indicador de status do WebSocket na navbar." },
  NAVBAR_LANGUAGE: { ...navbarItem("language"), description: "Seletor de idioma na navbar." },
  NAVBAR_THEME: { ...navbarItem("theme"), description: "Alternador de tema (claro/escuro) na navbar." },
  NAVBAR_VOLUME: { ...navbarItem("volume"), description: "Controle de volume das notificações na navbar." },
  NAVBAR_REFRESH: { ...navbarItem("refresh"), description: "Botão de recarregar a página na navbar." },
  NAVBAR_NOTIFICATIONS: { ...navbarItem("notifications"), description: "Sino de notificações na navbar." },
  NAVBAR_ANNOUNCEMENTS: { ...navbarItem("announcements"), description: "Popover de anúncios na navbar." },
  NAVBAR_CHAT: { ...navbarItem("chat"), description: "Popover do chat interno na navbar." },
  OVERLAY: {
    name: "app-overlay",
    description:
      "Camada em cima do app inteiro (inclusive navbar e menu). Enquanto houver uma action aqui o usuário NÃO " +
      "alcança as demais telas — onboarding obrigatório, aceite pendente, bloqueio. Não tem botão de fechar: " +
      "quem decide que acabou é o plugin, parando de declarar a action. O app faz POST no endpoint e injeta o HTML.",
    context: {
      idioma: t.note(t.string, '"pt" | "en" | "es"…'),
      tema: t.note(temaDesc, "cores do MUI já resolvidas para o modo ativo"),
    },
  },
  DASHBOARD_WIDGETS: {
    name: "dashboard-widgets",
    description:
      "Painel principal (Dashboard), ao final da grade de cards. Plugin com métrica própria entra como mais um " +
      "card; o conteúdo é do plugin (JSX ou HTML), o app só dá o lugar na grade.",
    context: { companyId: t.number },
  },
});

export const app = {
  /** Slots de render — derivado de `appSlots`. */
  slots: slotNamesOf(appSlots),
  /** @deprecated Formato antigo, derivado do registro. Use `appSlots`. */
  slotContext: legacySlotContextOf(appSlots),
  registry: appSlots,
} as const;

export type AppSlotContextMap = SlotContextMapOf<typeof appSlots>;
/** app-overlay. */
export type AppOverlayContext = AppSlotContextMap["app-overlay"];
